import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import type {
  DataResponse,
  MaterialCenaOverride,
  MaterialCenaOverrideUpsert,
} from "@/api/schemas";

/**
 * Ceny materiálov upravené LEN pre konkrétnu zákazku. Cenník ostáva
 * nedotknutý — do neho sa zapisuje samostatne (PATCH /cennik/materialy).
 */

function overridesKey(zakazkaId: string) {
  return [...queryKeys.zakazky.detail(zakazkaId), "cena-overrides"] as const;
}

export function useZakazkaCenaOverrides(
  zakazkaId: string | undefined,
): UseQueryResult<DataResponse<MaterialCenaOverride[]>> {
  return useQuery({
    queryKey: overridesKey(zakazkaId ?? "_"),
    enabled: !!zakazkaId,
    queryFn: async () => {
      const { data } = await api.get<DataResponse<MaterialCenaOverride[]>>(
        `/zakazky/${zakazkaId}/cena-overrides`,
      );
      return data;
    },
    staleTime: 30_000,
  });
}

/** Mapa material_id → override pre rýchle zobrazenie efektívnych cien. */
export function useCenaOverrideMap(
  zakazkaId: string | undefined,
): Map<string, MaterialCenaOverride> {
  const q = useZakazkaCenaOverrides(zakazkaId);
  const map = new Map<string, MaterialCenaOverride>();
  for (const o of q.data?.data ?? []) map.set(o.material_id, o);
  return map;
}

/** Efektívne cenové polia materiálu po aplikovaní override. */
export function applyCenaOverride<
  T extends {
    nakup_cena: string | number;
    rezia_pct?: string | number | null;
    praca_eur_per_mj?: string | number | null;
    nadmiera_pct?: string | number | null;
  },
>(m: T, ov: MaterialCenaOverride | undefined): T & { maOverride: boolean } {
  if (!ov) return { ...m, maOverride: false };
  return {
    ...m,
    nakup_cena: ov.nakup_cena ?? m.nakup_cena,
    rezia_pct: ov.rezia_pct ?? m.rezia_pct,
    praca_eur_per_mj: ov.praca_eur_per_mj ?? m.praca_eur_per_mj,
    nadmiera_pct: ov.nadmiera_pct ?? m.nadmiera_pct,
    maOverride: true,
  };
}

export function useUpsertZakazkaCenaOverride(
  zakazkaId: string,
): UseMutationResult<
  DataResponse<MaterialCenaOverride>,
  Error,
  { materialId: string; data: MaterialCenaOverrideUpsert }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ materialId, data }) => {
      const r = await api.put<DataResponse<MaterialCenaOverride>>(
        `/zakazky/${zakazkaId}/cena-overrides/${materialId}`,
        data,
      );
      return r.data;
    },
    // Ceny vstupujú do breakdownu celej zákazky aj do pravých súhrnov
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.zakazky.all });
      void qc.invalidateQueries({ queryKey: queryKeys.varianty.all });
    },
  });
}

export function useDeleteZakazkaCenaOverride(
  zakazkaId: string,
): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (materialId: string) => {
      await api.delete(`/zakazky/${zakazkaId}/cena-overrides/${materialId}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.zakazky.all });
      void qc.invalidateQueries({ queryKey: queryKeys.varianty.all });
    },
  });
}
